// @flow

import React from 'react';
import { connect } from 'react-redux';
import { I18n, Translate } from 'react-redux-i18n';
import { withRouter } from 'react-router-dom';
import CurrencySurfaceSwitcher from '../../../../common/CurrencySurfaceSwitcher';
import Analysis from '../Analysis';
import Comparables from './Comparables';
import type {
  Comparable,
  ComparableKpi,
  kpiData,
  StoreGemini,
  VisioglobeUnitsMap, VisioglobeUnitsOk,
} from '../../../../../types/geminiType';
import { COUNTRIES } from '../../../../../const';
import geminiApi from '../../../../../network/api/geminiApi';
import { getCurrentPcId, getShoppingCenterNameByPcCode } from '../../../../../utils/urlUtils';
import RippleButton from '../../../../../components/RippleButton';
import { getOkUnits } from '../geminiFunctions';
import SearchSelect from '../../../../store/data/components/SearchSelect';

type Props = {
  selectedStore: StoreGemini,
  selectedData: kpiData[],
  visioglobeUnitsMap: VisioglobeUnitsMap,
  shoppingCenterName: string,
}
type State = {
  selectedCat: string,
  selectedCountryCodeList: string[],
  comparable: ?Comparable,
  customComparables: ComparableKpi[],
  searchWord: string,
  showFilters: boolean,
}

const categoriesFilter = { ALL: 'all', COUNTRY: 'country', CATEGORY: 'category' };

class ComparablesModalContent extends React.Component<Props, State> {
  state: State = {
    selectedCat: categoriesFilter.ALL,
    selectedCountryCodeList: [],
    comparable: null,
    customComparables: [],
    searchWord: '',
    showFilters: false,
  };

  componentDidMount(): void {
    this.updateData(this.props.selectedStore);
  }

  updateData = (store: StoreGemini) => {
    if (store && store.codstr) {
      geminiApi.fetchStoresComparable(store.codstr, store.retailerId, getCurrentPcId())
        .then(res => res.json())
        .then(json => this.setState({ comparable: json }))
        .catch(() => this.setState({ comparable: null }));
    }
  };

  shouldComponentUpdate(nextProps: Props, nextState: State, nextContext: any) {
    if ((nextProps.selectedStore && !this.props.selectedStore) ||
      (nextProps.selectedStore && this.props.selectedStore
        && nextProps.selectedStore.codstr !== this.props.selectedStore.codstr)) {
      this.updateData(nextProps.selectedStore);
    }
    if (!nextProps.selectedStore) {
      nextState.comparable = null;
    }
    return true;
  }

  addCustomComparable = (customComparable: ComparableKpi) => {
    this.setState({ customComparables: [...this.state.customComparables, customComparable] });
  }

  addManuallyShoppingCenter = (codstr: number) => {
    const comparableSc = this.state.comparable.storeComparables
      .find(comparable => comparable.codstr === codstr);
    this.addCustomComparable(comparableSc);
  }

  // Flatten visioglobeUnits to have the same structure as ComparableKpi
  visioglobeUnitToComparableKpi = (visioglobe: VisioglobeUnitsOk) =>
    ({
      ...visioglobe,
      ...visioglobe.kpi,
      comparableName: visioglobe.storeName,
      comparableSC: this.props.shoppingCenterName || '',
    });

  addManuallyStore = (visioglobeCodstr: number) => {
    const visioglobeStore = this.getVisioglobeUnits()
      .find(visioglobeUnit => visioglobeUnit.codstr === visioglobeCodstr);
    this.addCustomComparable(this.visioglobeUnitToComparableKpi(visioglobeStore));
  }

  removeManuallyShoppingCenter = (codstr: number) => {
    const customComparables = this.state.customComparables
      .filter((customComparable: ComparableKpi) => customComparable.codstr !== codstr);
    this.setState({ customComparables });
  }

  updateShowFilters = () => this.setState({ showFilters: !this.state.showFilters });

  getVisioglobeUnits = () => getOkUnits(this.props.visioglobeUnitsMap);

  showFilters = () => (
    <div className="comparables-main-retailer filter-comparables-container">
      <div className="filter-comparables">
        <div className="comparables-header">
          <span className="store-name">{I18n.t('COMPARABLE.filtersTitle')}</span>
          <RippleButton
            className="apollo-button-contained"
            onClick={this.updateShowFilters}
          >
            {I18n.t('COMPARABLE.hideFilters')}
          </RippleButton>
        </div>
        <div className="filter">
          <div className="filter-header"><Translate value="COMPARABLE.typesOfComparable" /></div>
          <div className="category-selector">
            <button
              className={this.state.selectedCat === categoriesFilter.ALL ? 'selected' : ''}
              onClick={() => this.setState({ selectedCat: categoriesFilter.ALL })}
            >
              <Translate value="COMPARABLE.all" />
            </button>
            <button
              className={this.state.selectedCat === categoriesFilter.COUNTRY ? 'selected' : ''}
              onClick={() => this.setState({ selectedCat: categoriesFilter.COUNTRY })}
            >
              <Translate value="COMPARABLE.sameCountry" />
            </button>
            <button
              className={this.state.selectedCat === categoriesFilter.CATEGORY ? 'selected' : ''}
              onClick={() => this.setState({ selectedCat: categoriesFilter.CATEGORY })}
            >
              <Translate value="COMPARABLE.sameCategory" />
            </button>
          </div>
        </div>

        <div className="filter">
          <div className="filter-header"><Translate value="SIDE_MENU.CURRENCY_LOWER" /></div>
          <CurrencySurfaceSwitcher displaySelector />
        </div>

        {
          this.state.selectedCat !== categoriesFilter.COUNTRY && (
            <div className="filter">
              <div className="filter-header"><Translate value="COMPARABLE.countries" /></div>
              <select
                className="filter-select"
                onChange={(e) => {
                  this.setState({ selectedCountryCodeList: e.target.value });
                }}
              >
                <option value={[]}>{I18n.t('COMPARABLE.all')}</option>
                {
                  Object.keys(COUNTRIES).map(countryKey => (
                    <option
                      key={countryKey}
                      value={COUNTRIES[countryKey].countryCodeList}
                    >
                      {COUNTRIES[countryKey].label}
                    </option>
                  ))
                }
              </select>
            </div>
          )
        }

        {
          this.state.comparable
          && this.state.comparable.storeComparables && (
            <React.Fragment>
              <div className="filter">
                <div className="filter-header"><Translate value="COMPARABLE.addCustomStore" /></div>
                <SearchSelect
                  selectSources={
                    this.getVisioglobeUnits().map(visioglobeUnit => ({
                      label: visioglobeUnit.retailerName,
                      id: visioglobeUnit.codstr,
                      selected: this.state.customComparables
                        .some(comparable => comparable.codstr === visioglobeUnit.codstr),
                    }))
                  }
                  placeholder={I18n.t('COMPARABLE.findStore')}
                  handleChange={(codstr: number) => {
                    if (this.state.customComparables
                      .some(comparable => comparable.codstr === codstr)
                    ) {
                      this.removeManuallyShoppingCenter(codstr);
                    } else {
                      this.addManuallyStore(codstr);
                    }
                  }}
                />
              </div>
              <div className="filter">
                <div className="filter-header"><Translate value="COMPARABLE.chooseCustomSc" /></div>
                <SearchSelect
                  selectSources={
                    this.state.comparable.storeComparables
                    .filter((store: ComparableKpi) =>
                      getShoppingCenterNameByPcCode(store.pcCode).toLowerCase()
                      .includes(this.state.searchWord.toLowerCase()))
                    .map((store: ComparableKpi) => ({
                    label: getShoppingCenterNameByPcCode(store.pcCode),
                    id: store.codstr,
                    selected: this.state.customComparables
                      .some(comparable => comparable.codstr === store.codstr),
                  }))
                  }
                  placeholder={I18n.t('COMPARABLE.findSc')}
                  handleChange={(codstr: number) => {
                    if (this.state.customComparables
                      .some(comparable => comparable.codstr === codstr)
                    ) {
                      this.removeManuallyShoppingCenter(codstr);
                    } else {
                      this.addManuallyShoppingCenter(codstr);
                    }
                  }}
                />
              </div>
            </React.Fragment>
          )
        }
      </div>
    </div>
  );

  render() {
    return (
      <div className="modal-comparables-container">
        <div className="comparables-container">
          {
            this.state.showFilters ? (
              this.showFilters()
            ) : (
              <div className="comparables-main-retailer">
                <div className="comparables-header">
                  <span className="store-name">{this.props.selectedStore.storeName}</span>
                  <RippleButton
                    className="apollo-button-contained"
                    onClick={this.updateShowFilters}
                  >
                    {I18n.t('COMPARABLE.showFilters')}
                  </RippleButton>
                </div>
                <Analysis selectedData={this.props.selectedData} isButton={false} />
              </div>
            )
          }
          <Comparables
            storeGemini={this.props.selectedStore}
            selectedCat={this.state.selectedCat}
            selectedCountryCodeList={this.state.selectedCountryCodeList}
            customComparables={this.state.customComparables}
            removeManuallyShoppingCenter={this.removeManuallyShoppingCenter}
            comparable={this.state.comparable}
          />
        </div>
      </div>
    );
  }
}

export default withRouter(connect(state => ({
  shoppingCenterList: state.shoppingCenterFilters.filteredShoppingCenterList,
}), {})(ComparablesModalContent));
